import React from 'react';
import {
  Box, HStack, Text, Icon, Pressable, Tooltip, TooltipContent, TooltipText,
} from '@gluestack-ui/themed';
import {
  Target, CalendarOff, Info,
} from 'lucide-react-native';
import type { PacingData } from '../hooks/useJourneyPacing';

interface PacingHeaderProps {
  pacing: PacingData | null;
  onWeeksChange: (weeks: number | null) => void;
  targetWeeks: number | null;
}

/**
 * PacingHeader
 * ------------
 * Shows progress towards a target date and allows adjusting the pace.
 */
export const PacingHeader: React.FC<PacingHeaderProps> = ({ pacing, onWeeksChange, targetWeeks }) => {
  const weekOptions = [2, 4, 6, 8];

  return (
    <Box px="$4" py="$3" borderBottomWidth={1} borderColor="$borderLight" bg="$bgApp">
      <HStack alignItems="center" justifyContent="space-between">
        <HStack space="md" alignItems="center">
          <HStack space="xs" alignItems="center">
            <Icon as={pacing ? Target : CalendarOff} size="2xs" color={pacing ? '$brandBase' : '$textSubtle'} />
            <Text size="xs" color={pacing ? '$textHeading' : '$textSubtle'} fontWeight="bold">
              {pacing ? `Target: ${pacing.targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'Pacing Disabled'}
            </Text>
          </HStack>
          {/* Removed On Track / Behind pill as MVP shortcut since we don't track start dates yet */}
        </HStack>

        <HStack space="xs" alignItems="center">
          <HStack space="xs" alignItems="center">
            <Text size="2xs" color="$textSubtle">
              Pace:
            </Text>
            <Tooltip
              placement="top"
              trigger={(triggerProps) => (
                <Pressable {...triggerProps}>
                  <Icon as={Info} size="xs" color="$textSubtle" />
                </Pressable>
              )}
            >
              <TooltipContent maxWidth={250} bg="$backgroundDark800">
                <TooltipText>
                  In what timeframe do you want to be done? We will take your remaining tasks
                  and distribute them across this remaining time based on their estimated effort.
                </TooltipText>
              </TooltipContent>
            </Tooltip>
          </HStack>
          <Box w={4} />
          {weekOptions.map((w) => (
            <Pressable key={w} onPress={() => onWeeksChange(w)}>
              {({ pressed }) => (
                <Box
                  px="$2.5"
                  py="$1"
                  borderRadius="$sm"
                  borderWidth={1}
                  borderColor={targetWeeks === w ? '$brandBase' : '$borderLight'}
                  bg={targetWeeks === w ? '$brandBase' : '$bgApp'}
                  opacity={pressed ? 0.8 : 1}
                >
                  <Text size="2xs" fontWeight="bold" color={targetWeeks === w ? '$white' : '$textSubtle'}>
                    {w}
                    w
                  </Text>
                </Box>
              )}
            </Pressable>
          ))}
          <Pressable onPress={() => onWeeksChange(null)}>
            {({ pressed }) => (
              <Box
                px="$2.5"
                py="$1"
                borderRadius="$sm"
                borderWidth={1}
                borderColor={targetWeeks === null ? '$brandBase' : '$borderLight'}
                bg={targetWeeks === null ? '$brandBase' : '$bgApp'}
                opacity={pressed ? 0.8 : 1}
              >
                <Text size="2xs" fontWeight="bold" color={targetWeeks === null ? '$white' : '$textSubtle'}>
                  Off
                </Text>
              </Box>
            )}
          </Pressable>
        </HStack>
      </HStack>
    </Box>
  );
};

export default PacingHeader;
